/**
 * Стан області (design-system/stability.md, розд. 2; baseline А2–А5): один примітив замість того,
 * щоб кожен екран сам вирішував «порожньо / завантаження».
 *
 * - **Старе — до готовності нового.** Дані є (зокрема попередні за `placeholderData:
 *   keepPreviousData` на зміну ключа) — область показує їх, приглушено (`stale`), а не скелетон.
 * - **Поріг 300 мс.** Даних немає — до 300 мс нічого не міняється; далі скелетон області, з 3 с —
 *   «Довше, ніж зазвичай…», з 10 с — тайм-аут зі «Спробувати ще раз» (як `Pending`).
 * - **Мінімум показу 300 мс.** Скелетон, що вже з'явився, не зникає раніше: інакше він блимає.
 * - **«Порожньо» — лише після відповіді.** Порожні дані показуються як порожній стан, тільки коли
 *   запит завершився, це не дані-заглушка й людина не набирає (`typing`, пауза набору).
 * - Фонове оновлення (`isFetching` з даними) DOM не міняє: скелетон — лише без даних (А2).
 *
 * Джерело — результат `useQuery` / `useInfiniteQuery` або власний стан у тій самій формі
 * (`AreaSource`): примітив дизайн-системи не залежить від клієнта запитів.
 */
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { states as t } from '../i18n/uk/states.ts';
import { type AreaClock, AreaTimeline, realClock } from './area-timeline.ts';
import { announce } from './notify/store.ts';
import { PendingSlow, PendingTimeout } from './Pending.tsx';

/** Те, що примітив бере із запиту: підмножина `UseQueryResult` TanStack Query. */
export interface AreaSource<T> {
  data: T | undefined;
  status: 'pending' | 'error' | 'success';
  /** `idle` — запит не йде (зокрема вимкнений): без даних це не очікування, а «нічого». */
  fetchStatus?: 'fetching' | 'paused' | 'idle';
  /** Дані — попередній ключ (`keepPreviousData`) чи заглушка: показуємо приглушено. */
  isPlaceholderData?: boolean;
  refetch?: () => unknown;
}

export interface AreaOptions<T> {
  /** Чи дані порожні (порожній стан області). Типово — порожній масив. */
  isEmpty?: (data: T) => boolean;
  /** Людина набирає (пауза набору ще не минула): «порожньо» не показуємо. */
  typing?: boolean;
  /** Таймери шкали; типово — справжні. Тест передає ручні. Значення має бути сталим. */
  clock?: AreaClock;
  /**
   * Поріг скелетона (типово 300 мс) і вже відчекане до монтування: маршрутне очікування
   * (`useRoutePending`) передає 0 і `defaultPendingMs` роутера. Беруться лише на першому рендері.
   */
  thresholdMs?: number;
  waitedMs?: number;
}

/**
 * - `wait` — до порогу або запит вимкнений: показувати нічого (на першому показі) — старий вміст
 *   лишається, бо з даними вид не `wait`;
 * - `skeleton` — скелетон області (`slow` — ще й «Довше, ніж зазвичай…»);
 * - `timeout` — 10 с без відповіді; `error` — помилка без даних;
 * - `empty` — запит завершився, даних немає; `content` — дані (можливо, `stale`).
 */
export type AreaView = 'wait' | 'skeleton' | 'timeout' | 'error' | 'empty' | 'content';

export interface AreaState<T> {
  view: AreaView;
  data: T | undefined;
  /** Показано старі дані, поки йде новий запит чи набір: область приглушена (`data-stale`). */
  stale: boolean;
  /** Скелетон довше 3 с. */
  slow: boolean;
  /** Повтор після тайм-ауту: новий відлік і `refetch` джерела. */
  retry: () => void;
}

const emptyArray = (data: unknown) => Array.isArray(data) && data.length === 0;

export function useAreaState<T>(source: AreaSource<T>, options: AreaOptions<T> = {}): AreaState<T> {
  const { isEmpty = emptyArray, typing = false, clock = realClock } = options;
  const [, rerender] = useState(0);
  const [timeline] = useState(
    () =>
      new AreaTimeline(() => rerender((n) => n + 1), clock, {
        ...(options.thresholdMs === undefined ? {} : { thresholdMs: options.thresholdMs }),
        ...(options.waitedMs === undefined ? {} : { waitedMs: options.waitedMs }),
      }),
  );
  useEffect(() => () => timeline.dispose(), [timeline]);

  const { data, status, fetchStatus = 'idle', isPlaceholderData = false } = source;
  const settled = status !== 'pending' && fetchStatus !== 'fetching' && !isPlaceholderData;
  const empty = data !== undefined && isEmpty(data);
  // Порожні дані до завершення запиту чи під час набору — ще не відповідь: чекаємо, як без даних.
  const usable = data !== undefined && (!empty || (settled && !typing));
  const loading = status === 'pending' ? fetchStatus !== 'idle' : fetchStatus === 'fetching';
  // Повтор після помилки без даних — теж очікування: до порогу лишається помилка, далі скелетон.
  const waiting = !usable && (loading || typing);

  // До малювання: шкала знає про очікування в тому ж кадрі, тож поріг рахується від зміни.
  useLayoutEffect(() => timeline.set(waiting), [timeline, waiting]);

  const refetch = useRef(source.refetch);
  refetch.current = source.refetch;
  const retry = () => {
    timeline.restart();
    refetch.current?.();
  };

  const phase = timeline.phase;
  const base = { data, stale: false, slow: false, retry };
  if (timeline.skeletonVisible(usable))
    return { ...base, view: 'skeleton', slow: phase === 'slow' };
  if (usable) {
    const view = empty ? 'empty' : 'content';
    return { ...base, view, stale: !empty && (isPlaceholderData || typing) };
  }
  if (waiting && phase === 'timeout') return { ...base, view: 'timeout' };
  if (status === 'error') return { ...base, view: 'error' };
  return { ...base, view: 'wait' };
}

/**
 * Пауза набору: `true`, поки `value` мінялось менше `ms` тому (і в першому кадрі після зміни).
 * Порожній стан пошуку чекає її разом із відповіддю (палітра, пошук списку). `clock` — таймери, як
 * у `useAreaState`: типово справжні, тест передає ручні. Значення має бути сталим.
 */
export function useTypingPause(value: string, ms: number, clock: AreaClock = realClock): boolean {
  const [settledValue, setSettledValue] = useState(value);
  useEffect(() => {
    if (value === settledValue) return;
    return clock.after(ms, () => setSettledValue(value));
  }, [value, settledValue, ms, clock]);
  return value !== settledValue;
}

interface AreaProps<T> {
  state: AreaState<T>;
  /** Скелетон області у формі вмісту (`SkeletonTable`, `SkeletonLines`…). */
  skeleton: ReactNode;
  /** Порожній стан (`EmptyState`, `StateView`); без нього — нічого. */
  empty?: ReactNode;
  /** Помилка без даних; без нього — нічого (помилку показує викликач деінде). */
  error?: ReactNode;
  children: (data: T, meta: { stale: boolean }) => ReactNode;
}

/**
 * Область за станом `useAreaState`. Обгортки в DOM немає: приглушення старих даних (`data-stale`)
 * ставить сам вміст через `meta.stale`, щоб верстка не залежала від стану.
 */
export function Area<T>({ state, skeleton, empty, error, children }: AreaProps<T>) {
  const { view, slow } = state;
  useEffect(() => {
    if (view === 'skeleton') announce(slow ? t.loadingSlow : t.loading);
  }, [view, slow]);

  switch (view) {
    case 'wait':
      return null;
    case 'skeleton':
      return (
        <>
          {skeleton}
          {slow && <PendingSlow />}
        </>
      );
    case 'timeout':
      return <PendingTimeout onRetry={state.retry} />;
    case 'error':
      return <>{error ?? null}</>;
    case 'empty':
      return <>{empty ?? null}</>;
    case 'content':
      return <>{children(state.data as T, { stale: state.stale })}</>;
  }
}
